import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Badge } from '@/shared/ui/badge';
import { ArrowLeft, AlertCircle, Send } from 'lucide-react';
import ClausesConsentement from '@/shared/components/ClausesConsentement';
import ChoixAgregateurMandat from '@/shared/components/ChoixAgregateurMandat';
import { choixEnvoye } from '@/shared/utils/agregateurMandat';

export default function MandatPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // LES TROIS AUTORISATIONS, et elles seules depuis le 02/09/2026. `cgv`, `rgpd` et
  // `exclusivite` ont ete retirees de l'ecran avec leur case, et de `CONSENT_KEYS` cote
  // serveur dans le meme geste : garder la cle ici l'aurait envoyee a `false`, c'est-a-
  // dire enregistre un REFUS sur une question qu'on ne pose plus.
  //
  // ⚠️ `allConsentsChecked` exige que TOUTES les valeurs soient vraies : une cle
  // orpheline qu'aucune case ne coche eteindrait « Signer le mandat » pour toujours.
  const [consents, setConsents] = useState({
    modulation: false, donnees: false, audit: false,
  });
  const [selectedAssets, setSelectedAssets] = useState([]);
  // L'agregateur du mandat, choisi AVANT l'envoi du lien de signature (C8a).
  const [agregateur, setAgregateur] = useState('');
  // LE SECOND CHEMIN, ET IL EST LE PLUS FREQUENT DEPUIS CET ECRAN.
  //
  // Le canvas ci-dessous suppose que le client est LA, devant le poste. Depuis le CRM
  // il ne l'est presque jamais : ce qui etait signe ici etait, au mieux, un paraphe
  // trace par le commercial. Le lien envoie le client sur notre page publique, ou il
  // signe lui-meme sur le meme canvas — sans compte, depuis son telephone.
  const [envoiLien, setEnvoiLien] = useState(false);
  const [lienEnvoye, setLienEnvoye] = useState('');

  useEffect(() => {
    api.get(`/clients/${id}`).then(res => {
      setClient(res.data);
      const assets = res.data.assets || [];
      setSelectedAssets(assets.filter(a => a.statut_mandat !== 'actif').map(a => a.id));
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const allConsentsChecked = Object.values(consents).every(Boolean);

  const toggleAsset = (assetId) => {
    setSelectedAssets(prev =>
      prev.includes(assetId) ? prev.filter(x => x !== assetId) : [...prev, assetId]
    );
  };

  // FAIRE SIGNER A DISTANCE : le mandat naît SANS paraphe, puis le lien part.
  //
  // ⚠️ IL NE FAUT SURTOUT PAS ENVOYER `signature_data` ICI. Un mandat cree avec un
  // paraphe serait deja « signe » et le client signerait par-dessus une signature qui
  // n'est pas la sienne. Le service d'emission remet d'ailleurs `date_signature` a
  // vide : un mandat qui part en signature n'est pas un mandat signe, et cette date
  // est imprimee sur le PDF puis deposee chez Symphonics.
  const envoyerLien = async () => {
    if (!allConsentsChecked) {
      setError('Tous les consentements doivent etre coches');
      return;
    }
    setError('');
    setEnvoiLien(true);
    try {
      const { data } = await api.post('/mandats', {
        client_id: id,
        asset_ids: selectedAssets,
        consentement_modulation: consents.modulation,
        consentement_donnees: consents.donnees,
        consentement_audit: consents.audit,
        agregateur: choixEnvoye(agregateur),
      });
      const envoi = await api.post(`/mandats/${data.id}/lien-signature`, {});
      setLienEnvoye(envoi.data?.message || 'Lien de signature envoye au client.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail
               : "Le lien de signature n'a pas pu etre envoye");
    }
    setEnvoiLien(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!client) return null;

  if (lienEnvoye) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"
           data-testid="lien-signature-envoye">
        <Card className="max-w-md w-full border-sky-200 bg-sky-50/30">
          <CardContent className="p-8 text-center">
            <Send className="w-14 h-14 text-sky-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Lien de signature envoye
            </h2>
            <p className="text-sm text-slate-600 mb-4">{lienEnvoye}</p>
            <p className="text-[12px] text-slate-500 mb-4">
              Le mandat reste en attente tant que {client.prenom} n'a pas signe. Vous le
              retrouverez dans ses documents une fois la signature revenue.
            </p>
            <Button onClick={() => navigate(`/clients/${id}`)}
                    data-testid="retour-apres-lien">
              Retour a la fiche client
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const assets = client.assets || [];
  const eligibleAssets = assets.filter(a => a.statut_mandat !== 'actif');

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-3xl mx-auto" data-testid="mandat-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${id}`)} data-testid="back-from-mandat-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Signature du mandat
          </h1>
          {/* Le sous-titre nommait « Mandat de flexibilite Survoltage » (item B1).
              L'agregateur est attribue au client APRES la signature : l'annoncer ici
              associait la piece a quelqu'un qui n'avait pas encore ete choisi.
              C8a : il se choisit desormais explicitement plus bas ; le sous-titre reste
              neutre, il vaut aussi pour un mandat generique. */}
          <p className="text-sm text-slate-500">Consentement pour la flexibilite electrique</p>
        </div>
      </div>

      {/* Client Info Summary */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm uppercase tracking-[0.15em] text-slate-400 font-medium">
            Informations client
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-500">Nom: </span><span className="font-medium">{client.civilite} {client.prenom} {client.nom}</span></div>
          <div><span className="text-slate-500">Email: </span><span className="font-medium">{client.email}</span></div>
          <div><span className="text-slate-500">Adresse: </span><span className="font-medium">{client.adresse}, {client.code_postal} {client.ville}</span></div>
          <div><span className="text-slate-500">Telephone: </span><span className="font-medium">{client.telephone}</span></div>
          <div><span className="text-slate-500">PDL/PRM: </span><span className="font-medium font-mono">{client.pdl_prm || 'Non renseigne'}</span></div>
        </CardContent>
      </Card>

      {/* Eligibility check */}
      {(!client.chauffage_electrique || !client.gestionnaire_enedis) && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-700">Client ineligible</p>
              <p className="text-sm text-red-600">
                {!client.chauffage_electrique && 'Chauffage non 100% électrique. '}
                {!client.gestionnaire_enedis && 'Gestionnaire reseau non Enedis.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Asset Selection */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm uppercase tracking-[0.15em] text-slate-400 font-medium">
            Actifs concernes ({selectedAssets.length} selectionne(s))
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {eligibleAssets.length === 0 ? (
            /* LE MANDAT PRÉCÈDE L'ÉQUIPEMENT, il ne le suit pas.
               David, le 27/08 : « je signe un mandat chez toi, et une fois que tu
               achètes un truc, je peux exploiter ta maison ». L'écran exigeait au moins
               un actif : le bouton restait éteint et un message réclamait une sélection.
               L'installateur n'avait alors qu'une issue, inventer un équipement — donc
               écrire en base une machine qui n'existe pas, que la veille interrogera, et
               qui ne répondra jamais.
               Le cas est ANNONCÉ et pas seulement toléré : un écran qui laisse signer
               sans rien dire laisse croire à un oubli. */
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                 data-testid="mandat-sans-actif">
              <p className="text-sm font-medium text-slate-700">Aucun équipement enregistré</p>
              <p className="text-sm text-slate-500 mt-1">
                Le mandat peut être signé dès maintenant : il précède l&apos;installation.
                Les équipements posés plus tard seront rattachés à ce client sans nouvelle
                signature.
              </p>
            </div>
          ) : eligibleAssets.map(asset => (
            <label key={asset.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={selectedAssets.includes(asset.id)}
                onCheckedChange={() => toggleAsset(asset.id)}
                data-testid={`asset-checkbox-${asset.id}`}
              />
              <div className="flex-1">
                <p className="text-sm font-medium">{asset.type} - {asset.marque} {asset.modele}</p>
                <p className="text-xs text-slate-500">{asset.puissance_kw} kW | {asset.identifiant_equipement}</p>
              </div>
            </label>
          ))}
        </CardContent>
      </Card>

      {/* L'agregateur du mandat (C8a) : avant les consentements et l'envoi du lien. */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <ChoixAgregateurMandat valeur={agregateur} onChange={setAgregateur}
                                 testid="crm-agregateur-mandat" />
        </CardContent>
      </Card>

      {/* Consent Checkboxes */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm uppercase tracking-[0.15em] text-slate-400 font-medium">
            Consentements obligatoires
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* LE TEXTE N'EST PLUS ECRIT ICI (items B2 et consentement RGPD).
              Cinq libelles vivaient en dur sur cet ecran, et cinq autres, differents,
              sur celui du parcours terrain. Deux textes pour un meme engagement, c'est
              un vice de consentement. Ils viennent maintenant du serveur, qui en
              conserve l'empreinte : c'est ce qui permet de dire ce que le client a lu.

              Deux d'entre eux mentaient en plus sur la personne engagee : les clauses
              nommaient Survoltage, attribue seulement APRES la signature, et le
              consentement RGPD nommait « FlexCore », l'editeur du logiciel, alors que
              le client consent a etre recontacte par l'OPERATEUR. */}
          {/* ⚠️ LA CASE « PDL INTROUVABLE » A ETE RETIREE LE 02/09, ET ELLE NE REVIENT
              PAS SEULE. Elle disait « je mandate l'operateur pour effectuer la recherche
              de mon PDL aupres d'Enedis », et elle n'existait que sur cet ecran-ci.
              Demande de Boubacar, avec les trois clauses complementaires.

              Le champ `recherche_pdl` a quitte le mandat DANS LE MEME GESTE
              (`services/signup.py`). Le laisser en le posant a `false` aurait inscrit sur
              chaque mandat neuf que le client refuse une recherche qu'on ne lui propose
              plus. Les mandats anterieurs le portent encore et le PDF l'imprime pour eux
              seuls. */}
          <ClausesConsentement consents={consents} setConsents={setConsents} />
        </CardContent>
      </Card>

      {/* ⚠️ LE PARAPHEUR A ETE RETIRE LE 08/09/2026, SUR DECISION DE BOUBACAR.

          « Il faut enlever toute possibilite de signature dans le CRM car dans ce cas de
            figure, impossible d etre en presence du client. Donc seulement le lien doit
            fonctionner. »

          Ce cadre supposait le client devant ce poste. Depuis le CRM il n y est
          jamais : ce qui etait signe la etait, au mieux, le trait du commercial. Le
          client signe desormais lui-meme, sur sa propre page, par le lien qu il recoit.

          ⚠️ NE PAS LE REMETTRE « pour le cas ou le client passerait au bureau ». Ce cas
          a un chemin a lui, et c est le parcours terrain (CFTSign), ou le poseur fait
          signer devant lui avec la geolocalisation et l identite du compte qui mene le
          parcours. Un parapheur ici ne prouverait rien de tout cela.

          Le champ `signature_data` a quitte `MandatCreate` dans le meme geste : le front
          n est pas une frontiere, et le laisser aurait garde la porte ouverte a un appel
          direct. */}

      {/* Error & Sign */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="mandat-error">
          {error}
        </div>
      )}

      {/* UN SEUL CHEMIN DEPUIS CET ECRAN, et c est le lien.

          Il y en avait deux jusqu au 08/09 : le parapheur, qui supposait le client
          present, et le lien. Le premier a ete retire -- voir plus haut. */}
      <div className="flex flex-wrap justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(`/clients/${id}`)} data-testid="cancel-mandat-btn">
          Annuler
        </Button>
        <Button
          className="px-8"
          disabled={!allConsentsChecked || envoiLien}
          onClick={envoyerLien}
          data-testid="lien-signature-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          {envoiLien ? 'Envoi en cours...' : 'Envoyer le lien de signature'}
        </Button>
      </div>
      <p className="text-[12px] text-slate-500 text-right" data-testid="lien-signature-aide">
        Le client recevra un courriel à <strong>{client.email || 'son adresse'}</strong>{' '}
        avec un lien personnel pour signer lui-même, sans compte. Le mandat restera{' '}
        <strong>en attente</strong> tant qu&apos;il n&apos;aura pas signé.
      </p>
    </div>
  );
}
